import React, { useState } from "react";
import { Plus, Shield } from "lucide-react";
import { useSelector } from "react-redux";

import { RootState } from "../../../app/store";
import { selectChargeCardsForLoanReference } from "../Logic/chargesSlice";

import AddChargeModal from "./AddChargeModal";
import ChargeCard from "./ChargeCard";
import ManageChargeModal from "./ManageChargeModal";
import { getAccountProductInfoByAccountNumber } from "../../creditAccounts/logic/slice";
import { useAppSelector } from "../../../app/hooks";

interface ChargesSectionProps {
  loanReferenceId: string;
}

const ChargesSection: React.FC<ChargesSectionProps> = ({
  loanReferenceId,
}) => {
  const [isAddChargeModalOpen, setIsAddChargeModalOpen] = useState(false);
  const [selectedChargeId, setSelectedChargeId] = useState<string | null>(null);

  const loanCharges = useSelector((state: RootState) =>
    selectChargeCardsForLoanReference(state, loanReferenceId),
  );
  const accountProductInfo = useAppSelector((state) =>
    getAccountProductInfoByAccountNumber(state, loanReferenceId),
  )!;
  const displayAccountNumber = accountProductInfo.ext_account_number;

  const closeChargeModal = () => setIsAddChargeModalOpen(false);
  const closeManageModal = () => setSelectedChargeId(null);

  const emptyState = (
    <div className="flex flex-col items-center px-3 py-6">
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-[8px] border border-[#E5E5E5] bg-white p-2">
        <Shield size={24} strokeWidth={2} className="text-[#0A0A0A]" />
      </div>

      <div className="flex flex-col items-center gap-2">
        <h3 className="text-center text-[16px] font-semibold leading-6 text-[#0A0A0A]">
          No Charges on this loan yet
        </h3>

        <p className="max-w-[300px] text-center text-[14px] font-normal leading-5 text-[#737373]">
          Add the first charge - define the asset and the loans it covers
        </p>
      </div>

      <button
        type="button"
        onClick={() => setIsAddChargeModalOpen(true)}
        className="mt-3 flex h-9 items-center justify-center gap-2 rounded-[8px] bg-[linear-gradient(180deg,#788AF7_0%,#3551F3_100%)] 
         px-4 py-2 text-[14px] font-medium leading-5 text-[#FAFAFA] shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
      >
        <Plus size={16} strokeWidth={2} />
        <span>Add Charge</span>
      </button>
    </div>
  );

  return (
    <>
      <div className="rounded-[8px] border border-[#E5E5E5] bg-white p-3">
        {loanCharges.length > 0 && (
          <div className="flex items-center justify-between">
            <div
              title={displayAccountNumber}
              className="truncate text-[14px] font-medium leading-5"
            >
              <span className="text-[#737373]">Charges associated with LR:</span>{" "}
              <span className="text-[#0A0A0A]">{displayAccountNumber}</span>
            </div>
          </div>
        )}

        {loanCharges.length ? (
          <div className="mt-3 flex flex-col gap-2">
            {loanCharges.map((charge) => (
              <ChargeCard
                key={charge.id}
                chargeId={charge.id}
                onManage={setSelectedChargeId}
              />
            ))}
          </div>
        ) : (
          emptyState
        )}
      </div>

      {isAddChargeModalOpen && (
        <AddChargeModal
          onClose={closeChargeModal}
          onBack={closeChargeModal}
        />
      )}

      {selectedChargeId && (
        <ManageChargeModal
          chargeId={selectedChargeId}
          onClose={closeManageModal}
          onBack={closeManageModal}
        />
      )}
    </>
  );
};

export default ChargesSection;